const APP_HOSTS = new Set([
  'app.nynch.com',
  'nynch.com',
  'www.nynch.com',
  'localhost',
  '127.0.0.1',
  '::1',
]);

const APP_HOST_SUFFIXES = ['.nynch.com', '.vercel.app', '.supabase.co'];

export const ASSISTANT_DOMAIN_CNAME_TARGET = (
  import.meta.env.VITE_ASSISTANT_DOMAIN_CNAME_TARGET || 'cname.vercel-dns.com'
)
  .trim()
  .toLowerCase()
  .replace(/\.$/, '');

export function normalizeAssistantHostname(
  input: string | null | undefined,
): string | null {
  if (!input) return null;
  let hostname = input.trim().toLowerCase();
  hostname = hostname.replace(/^https?:\/\//, '');
  hostname = hostname.split('/')[0] ?? '';
  hostname = hostname.split(':')[0] ?? '';
  hostname = hostname.replace(/\.$/, '');
  if (!hostname) return null;
  if (
    !/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/.test(
      hostname,
    )
  ) {
    return null;
  }
  return hostname;
}

export function isNynchHostedAssistantHost(
  input: string | null | undefined,
): boolean {
  const hostname = normalizeAssistantHostname(input);
  if (!hostname) return true;
  if (APP_HOSTS.has(hostname)) return true;
  return APP_HOST_SUFFIXES.some((suffix) => hostname.endsWith(suffix));
}

export function shouldResolveAssistantHostname(
  input: string | null | undefined,
): boolean {
  const hostname = normalizeAssistantHostname(input);
  return Boolean(hostname && !isNynchHostedAssistantHost(hostname));
}

interface AssistantDomainShareCandidate {
  hostname: string;
  status: string;
}

interface ProspectBriefShareUrlArgs {
  publicSlug: string;
  sharedPublicSlug: string;
  assistantDomains: AssistantDomainShareCandidate[];
  fallbackOrigin: string;
}

/**
 * Prospecting Audits share the Growth Assistant's verified customer domain.
 * Until a custom domain is active, links stay on the current Nynch origin.
 */
export function buildProspectBriefShareUrl({
  publicSlug,
  sharedPublicSlug,
  assistantDomains,
  fallbackOrigin,
}: ProspectBriefShareUrlArgs): string {
  const activeHostname = assistantDomains
    .filter((domain) => domain.status === 'active')
    .map((domain) => normalizeAssistantHostname(domain.hostname))
    .find((hostname): hostname is string => Boolean(hostname));
  const origin = activeHostname
    ? `https://${activeHostname}`
    : fallbackOrigin.replace(/\/+$/, '');

  const canonicalSlug = activeHostname ? publicSlug : sharedPublicSlug;
  return `${origin}/audit/${encodeURIComponent(canonicalSlug)}`;
}

/**
 * Growth Agent URL for the Value Audit "Discuss these findings" CTA.
 *
 * On a customer's custom domain the agent is the site root (`/?audit=…`).
 * On Nynch-hosted pages it is `/assistant/<slug>?audit=…`. Returns null when
 * the handoff cannot be formed so the public page can keep the booking CTA.
 */
export function buildValueAuditDiscussHref(args: {
  handoffToken: string | null | undefined;
  slug: string | null | undefined;
  hostname: string | null | undefined;
}): string | null {
  const token = args.handoffToken?.trim();
  if (!token) return null;
  const auditParam = `audit=${encodeURIComponent(token)}`;
  if (shouldResolveAssistantHostname(args.hostname)) {
    return `/?${auditParam}`;
  }
  const slug = args.slug?.trim();
  if (!slug) return null;
  return `/assistant/${encodeURIComponent(slug)}?${auditParam}`;
}
